/**
 * DevConsole — In-game terminal overlay.
 *
 * Toggle: Cmd+Shift+C (Mac) / Ctrl+Shift+C (Win)
 * Close: ESC or same shortcut
 *
 * Commands — type "help" in console for full list.
 */

import { SaveManager } from '@/systems/SaveManager';
import { SoundManager } from '@/systems/SoundManager';
import { EventBus, GameEvents } from '@/events/EventBus';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { ResourceManager } from '@/systems/ResourceManager';
import { markMapCompleted } from '@/map/MapData';
import { GOD, saveGodMode } from '@/config/GodMode';
import { CinematicDirector, getSequence, getSequenceIds } from '@/cinematic/CinematicDirector';
import { CinematicEvents } from '@/cinematic/CinematicEvents';
import { ALL_COMPONENT_TYPES, COMPONENT_DEFS } from '@/components/ComponentDefs';
import { LEVEL_TREE } from '@/map/LevelTree';
import { resetEasterEggState } from '@/systems/EasterEggs';
import { ACHIEVEMENT_DEFS } from '@/config/AchievementDefs';
import { AchievementManager } from '@/systems/AchievementManager';
import type { GameScene } from '@/scenes/GameScene';

/** Stored reference to the Phaser game instance (set at boot) */
let gameRef: Phaser.Game | null = null;

/** Call once at boot to give DevConsole access to the game */
export function setGameRef(game: Phaser.Game): void {
  gameRef = game;
}

/** Event emitted when dev mode changes */
export const DEV_MODE_CHANGED = 'devModeChanged';

// ── Interactive mode flags (read by GameScene) ──
export let devDestroyMode = false;
export let devMoveMode = false;
export let devNukeMode = false;
export let devAchieveOnClick = false;

// ── DOM Elements ──
let overlay: HTMLDivElement | null = null;
let input: HTMLInputElement | null = null;
let logDiv: HTMLDivElement | null = null;
let isOpen = false;
let modeBadge: HTMLDivElement | null = null;

/** History of typed commands (arrow up/down) */
const history: string[] = [];
let historyIdx = -1;

// ── Helpers ──

function getGameScene(): GameScene | null {
  if (!gameRef) return null;
  const scene = gameRef.scene.getScene('GameScene');
  if (!scene || !scene.scene.isActive()) return null;
  return scene as GameScene;
}

function requireNumber(arg: string | undefined, name: string): { ok: false; msg: string } | { ok: true; value: number } {
  if (arg === undefined) return { ok: false, msg: `Missing argument: ${name}` };
  const n = Number(arg);
  if (isNaN(n)) return { ok: false, msg: `Invalid number: ${arg}` };
  return { ok: true, value: n };
}

function requireGameScene(): { ok: false; msg: string } | { ok: true; scene: GameScene } {
  const scene = getGameScene();
  if (!scene) return { ok: false, msg: 'GameScene not active' };
  return { ok: true, scene };
}

// ── Commands ──

/** Summary line showing all mode states (ON/OFF) */
function modesSummary(): string {
  const modes: [string, boolean][] = [
    ['invincible', GOD.infiniteHp],
    ['destroy', devDestroyMode],
    ['move', devMoveMode],
    ['nuke', devNukeMode],
  ];
  return modes.map(([name, on]) => `${name}: ${on ? 'ON' : 'OFF'}`).join('  |  ');
}

interface CmdResult { ok: boolean; msg: string }
type CmdHandler = (args: string[]) => CmdResult;

const commands: Record<string, CmdHandler> = {
  // ── Dev ──
  god: () => {
    const current = SaveManager.getSettings().devMode ?? false;
    SaveManager.updateSettings({ devMode: !current });
    EventBus.emit(DEV_MODE_CHANGED, !current);
    return { ok: true, msg: `Dev mode ${!current ? 'ON' : 'OFF'}` };
  },

  // ── Resources ──
  money: (args) => {
    if (args[0]?.toLowerCase() === 'reset') {
      ProgressionManager.addCurrency(-ProgressionManager.getCurrency());
      ProgressionManager.clearPreGameSnapshot();
      ProgressionManager.save();
      ResourceManager.reset();
      const gs = getGameScene();
      if (gs) {
        gs.addResources(-gs.resources);
      }
      return { ok: true, msg: 'All resources reset to 0' };
    }
    const r = requireNumber(args[0], 'amount');
    if (!r.ok) return r;
    const gs = getGameScene();
    if (gs) {
      // Replace in-game resources (set to value, not add)
      gs.addResources(r.value - gs.resources);
    } else {
      // Replace treasury (set to value, not add)
      const current = ProgressionManager.getCurrency();
      ProgressionManager.addCurrency(r.value - current);
      ProgressionManager.save();
      if (gameRef) {
        gameRef.registry.set('gameResources', r.value);
      }
    }
    // Replace Co & Electro (set to value, not add)
    ResourceManager.addCo(r.value - ResourceManager.co);
    ResourceManager.addElectro(r.value - ResourceManager.electro);
    return { ok: true, msg: `Set $${r.value}, Co ${r.value}, Electro ${r.value}` };
  },

  co: (args) => {
    const r = requireNumber(args[0], 'amount');
    if (!r.ok) return r;
    ResourceManager.addCo(r.value);
    return { ok: true, msg: `Added ${r.value} Co — total: ${ResourceManager.co}` };
  },

  electro: (args) => {
    const r = requireNumber(args[0], 'amount');
    if (!r.ok) return r;
    ResourceManager.addElectro(r.value);
    return { ok: true, msg: `Added ${r.value} Electro — total: ${ResourceManager.electro}` };
  },

  // ── Game ──
  speed: (args) => {
    const r = requireNumber(args[0], 'multiplier');
    if (!r.ok) return r;
    const gs = requireGameScene();
    if (!gs.ok) return gs;

    // Easter egg: Speed x999 Meltdown — absurd speed triggers visual chaos
    if (GOD.eeSpeedMeltdown && r.value >= 100) {
      gs.scene.time.timeScale = r.value;
      gs.scene.gameSpeed = r.value;

      // Stage 1: warning at 2s
      gs.scene.time.delayedCall(2000 / r.value, () => {
        EventBus.emit(GameEvents.EASTER_EGG_CRT_GLITCH, { duration: 300 });
        SoundManager.play('baseHit');
      });

      // Stage 2: escalating chaos
      const meltdownStages = [
        { delay: 4000, msg: '⚠ WARNING: THERMAL LIMIT APPROACHING' },
        { delay: 6000, msg: '⚠ WARNING: FRAME BUFFER OVERFLOW' },
        { delay: 8000, msg: '⚠ CRITICAL: REALITY ENGINE UNSTABLE' },
      ];
      for (const stage of meltdownStages) {
        gs.scene.time.delayedCall(stage.delay / Math.min(r.value, 10), () => {
          EventBus.emit(GameEvents.EASTER_EGG_CRT_GLITCH, { duration: 200 });
          EventBus.emit(GameEvents.EASTER_EGG_FLOATING_WARNING, { text: stage.msg, scene: gs.scene });
        });
      }

      // Stage 3: thermal throttle reset at speed >= 500
      if (r.value >= 500) {
        gs.scene.time.delayedCall(10000 / Math.min(r.value, 10), () => {
          // Flash white
          gs.scene.cameras.main?.flash(500, 255, 255, 255, true);
          EventBus.emit(GameEvents.EASTER_EGG_CRT_GLITCH, { duration: 800 });
          // Reset speed
          gs.scene.time.timeScale = 1;
          gs.scene.gameSpeed = 1;
          // Log the throttle message
          appendLog('🔥 Thermal Throttling Engaged. Speed reset to 1x.', '#ff6644');
          appendLog("The CPU sends its regards.", '#888888');
        });
      }

      const warning = r.value >= 500
        ? `Game speed: ${r.value}x — MELTDOWN IMMINENT. Brace yourself.`
        : `Game speed: ${r.value}x — You sure about this?`;
      return { ok: true, msg: warning };
    }

    gs.scene.time.timeScale = r.value;
    gs.scene.gameSpeed = r.value;
    return { ok: true, msg: `Game speed: ${r.value}x` };
  },

  wave: (args) => {
    const r = requireNumber(args[0], 'wave number');
    if (!r.ok) return r;
    const gs = requireGameScene();
    if (!gs.ok) return gs;
    const wm = gs.scene.waveManager;
    const target = Math.max(1, Math.min(r.value, wm.totalWaves));
    wm.currentWave = target - 1;
    return { ok: true, msg: `Set to wave ${target}/${wm.totalWaves} — will start on next wave trigger` };
  },

  hp: (args) => {
    const r = requireNumber(args[0], 'amount');
    if (!r.ok) return r;
    const gs = requireGameScene();
    if (!gs.ok) return gs;
    const base = gs.scene.playerBase;
    if (!base || !base.alive) return { ok: false, msg: 'No active player base' };
    // Heal base to full (heal distributes across nodes)
    const deficit = r.value - base.hp;
    if (deficit > 0) base.heal(deficit);
    return { ok: true, msg: `Base HP healed to ${base.hp}/${base.maxHp}` };
  },

  kill: (args) => {
    if (args[0]?.toLowerCase() !== 'all') return { ok: false, msg: 'Usage: kill all' };
    const gs = requireGameScene();
    if (!gs.ok) return gs;
    const enemies = gs.scene.waveManager.enemies.filter(e => e.alive);
    let count = 0;
    for (const e of enemies) { e.destroy(); count++; }
    return { ok: true, msg: `Killed ${count} enemies` };
  },

  // ── Progression ──
  unlock: (args) => {
    if (args[0]?.toLowerCase() !== 'all') return { ok: false, msg: 'Usage: unlock all' };
    GOD.unlockAll = !GOD.unlockAll;
    saveGodMode();
    EventBus.emit(GameEvents.COMPONENT_UNLOCKED);
    // Also unlock all campaign maps so player can access everything
    if (GOD.unlockAll) {
      for (const node of LEVEL_TREE) {
        if (!ProgressionManager.isUnlocked(node.id)) {
          (ProgressionManager as any).unlocked.add(node.id);
        }
      }
      ProgressionManager.save();
    }
    const state = GOD.unlockAll ? 'UNLOCKED' : 'LOCKED';
    const hint = GOD.unlockAll ? '→ open Workshop to see all components' : '→ back to normal progression';
    return { ok: true, msg: `All components ${state}. ${hint}` };
  },

  unlockmaps: () => {
    let count = 0;
    for (const node of LEVEL_TREE) {
      if (!ProgressionManager.isUnlocked(node.id)) {
        (ProgressionManager as any).unlocked.add(node.id);
        count++;
      }
    }
    (ProgressionManager as any).save();
    return { ok: true, msg: `Unlocked ${count} maps (${LEVEL_TREE.length} total). Go to Map Select to see them.` };
  },

  upgrade: (args) => {
    if (args[0]?.toLowerCase() !== 'all') return { ok: false, msg: 'Usage: upgrade all' };
    let count = 0;
    for (const type of ALL_COMPONENT_TYPES) {
      const def = COMPONENT_DEFS[type];
      if (!def || def.maxEvolutionLevel <= 0) continue;
      const current = ProgressionManager.getComponentLevel(type);
      if (current >= def.maxEvolutionLevel) continue;
      // Force-set to max level (bypass cost)
      for (let i = current; i < def.maxEvolutionLevel; i++) {
        (ProgressionManager as any).componentLevels.set(type, i + 1);
      }
      count++;
    }
    (ProgressionManager as any).save();
    return { ok: true, msg: `Upgraded ${count} components to max tier — restart workshop to see new palette cards` };
  },

  stars: (args) => {
    if (args.length < 2) return { ok: false, msg: 'Usage: stars <mapId> <0-3>' };
    const mapId = args[0];
    const r = requireNumber(args[1], 'stars');
    if (!r.ok) return r;
    const s = Math.max(0, Math.min(3, Math.round(r.value)));
    markMapCompleted(mapId, s);
    return { ok: true, msg: `Map "${mapId}" set to ${s} star(s)` };
  },

  reset: () => {
    ProgressionManager.reset();
    ResourceManager.reset();
    resetEasterEggState();
    return { ok: true, msg: 'Save reset — reload the page to apply' };
  },

  // ── Interactive modes ──
  invincible: () => {
    GOD.infiniteHp = !GOD.infiniteHp;
    updateModeBadge();
    return { ok: true, msg: modesSummary() };
  },

  destroy: () => {
    devDestroyMode = !devDestroyMode;
    if (devDestroyMode) { devMoveMode = false; devNukeMode = false; }
    updateCursor();
    updateModeBadge();
    return { ok: true, msg: modesSummary() };
  },

  move: () => {
    devMoveMode = !devMoveMode;
    if (devMoveMode) { devDestroyMode = false; devNukeMode = false; }
    updateCursor();
    updateModeBadge();
    return { ok: true, msg: modesSummary() };
  },

  nuke: () => {
    devNukeMode = !devNukeMode;
    if (devNukeMode) { devDestroyMode = false; devMoveMode = false; }
    updateCursor();
    updateModeBadge();
    return { ok: true, msg: modesSummary() };
  },

  // ── Cinematic ──
  cinematic: (args) => {
    const sub = args[0]?.toLowerCase();
    if (sub === 'reset') {
      CinematicDirector.resetCinematic('demo-wave-1');
      GOD.forceCinematic = true;
      saveGodMode();
      return { ok: true, msg: 'Cinematic reset. Reload level to replay.' };
    }
    if (sub === 'play') {
      CinematicDirector.resetCinematic('demo-wave-1');
      EventBus.emit(CinematicEvents.START, { sequenceId: 'demo-wave-1' });
      return { ok: true, msg: 'Cinematic started.' };
    }
    if (sub === 'skip') {
      EventBus.emit(CinematicEvents.SKIP);
      return { ok: true, msg: 'Cinematic skipped.' };
    }
    if (sub === 'edit' || !sub) {
      if (!gameRef) return { ok: false, msg: 'No game ref' };
      if (gameRef.scene.isActive('CinematicEditorScene')) {
        return { ok: false, msg: 'Editor already open.' };
      }
      // Find the topmost active non-overlay scene
      const OVERLAY_KEYS = new Set(['CinematicEditorScene', 'GodModeScene']);
      const activeScenes = gameRef.scene.getScenes(true);
      const host = activeScenes.find(s => !OVERLAY_KEYS.has(s.scene.key));
      if (!host) return { ok: false, msg: 'No active scene to overlay.' };
      const hostKey = host.scene.key;
      const callerScenes = [hostKey];
      if (hostKey === 'GameScene' && gameRef.scene.isActive('UIScene')) callerScenes.push('UIScene');

      // Pause caller scenes
      for (const key of callerScenes) host.scene.pause(key);

      const seqId = sub === 'edit' ? args[1] : undefined;
      const initData: Record<string, unknown> = { hostSceneKey: hostKey, callerScenes };
      if (seqId) {
        const seq = getSequence(seqId);
        if (!seq) {
          const ids = getSequenceIds();
          for (const key of callerScenes) host.scene.resume(key);
          return { ok: false, msg: `Sequence "${seqId}" not found. Available: ${ids.join(', ') || 'none'}` };
        }
        initData.sequence = seq;
      } else {
        const ids = getSequenceIds();
        if (ids.length > 0) initData.sequence = getSequence(ids[0]);
      }
      host.scene.launch('CinematicEditorScene', initData);
      close();
      return { ok: true, msg: `Cinematic Editor opened on ${hostKey}.` };
    }
    if (sub === 'list') {
      const ids = getSequenceIds();
      return { ok: true, msg: ids.length ? `Sequences: ${ids.join(', ')}` : 'No sequences registered.' };
    }
    return { ok: true, msg: 'Usage: cinematic [play | reset | skip | edit [id] | list]' };
  },

  // ── Help ──
  // ── Achievements ──
  achieve: (args) => {
    const sub = args[0]?.toLowerCase();

    // achieve list — show all achievements with unlock status
    if (!sub || sub === 'list') {
      const lines = ACHIEVEMENT_DEFS.map(d => {
        const unlocked = ProgressionManager.getAchievementTier(d.id) >= 1;
        const claimed = ProgressionManager.getAchievementClaimedReward(d.id) >= 1;
        const status = unlocked ? (claimed ? '✅' : '🎁') : '❌';
        return `${status} ${d.id.padEnd(22)} ${d.name}`;
      });
      return { ok: true, msg: `Achievements (${ACHIEVEMENT_DEFS.length}):\n${lines.join('\n')}\n\nUsage: achieve <id> | achieve all | achieve claim <id> | achieve claim all` };
    }

    // achieve onclick — toggle click-to-unlock mode in STATS panel
    if (sub === 'onclick') {
      devAchieveOnClick = !devAchieveOnClick;
      return { ok: true, msg: `Achieve-on-click ${devAchieveOnClick ? 'ON — click any achievement in STATS to unlock it' : 'OFF'}` };
    }

    // achieve all — unlock all achievements
    if (sub === 'all') {
      let count = 0;
      for (const def of ACHIEVEMENT_DEFS) {
        if (ProgressionManager.getAchievementTier(def.id) < 1) {
          ProgressionManager.setAchievementTier(def.id, 1);
          EventBus.emit(GameEvents.ACHIEVEMENT_UNLOCKED, { id: def.id, badge: def.badge, def });
          count++;
        }
      }
      return { ok: true, msg: count > 0 ? `Unlocked ${count} achievements!` : 'All achievements already unlocked.' };
    }

    // achieve claim <id|all> — force-claim rewards
    if (sub === 'claim') {
      const target = args[1]?.toLowerCase();
      if (target === 'all') {
        let count = 0;
        for (const def of ACHIEVEMENT_DEFS) {
          if (AchievementManager.isRewardClaimable(def.id)) {
            AchievementManager.claimReward(def.id);
            count++;
          }
        }
        return { ok: true, msg: count > 0 ? `Claimed ${count} rewards!` : 'No unclaimed rewards.' };
      }
      if (!target) return { ok: false, msg: 'Usage: achieve claim <id|all>' };
      const def = ACHIEVEMENT_DEFS.find(d => d.id === target);
      if (!def) return { ok: false, msg: `Unknown achievement: ${target}` };
      if (!AchievementManager.isRewardClaimable(def.id)) return { ok: false, msg: `"${def.name}" is not claimable (locked or already claimed).` };
      AchievementManager.claimReward(def.id);
      return { ok: true, msg: `Claimed reward for "${def.name}"!` };
    }

    // achieve <id> — unlock a specific achievement
    const def = ACHIEVEMENT_DEFS.find(d => d.id === sub);
    if (!def) return { ok: false, msg: `Unknown achievement: "${sub}". Type "achieve list" to see all IDs.` };
    if (ProgressionManager.getAchievementTier(def.id) >= 1) return { ok: true, msg: `"${def.name}" already unlocked.` };
    ProgressionManager.setAchievementTier(def.id, 1);
    EventBus.emit(GameEvents.ACHIEVEMENT_UNLOCKED, { id: def.id, badge: def.badge, def });
    return { ok: true, msg: `Unlocked "${def.name}" (${def.badge})!` };
  },

  help: () => {
    appendWelcome();
    return { ok: true, msg: '' };
  },
};

// ── Easter egg: funny responses for non-standard commands ──
const FUNNY_RESPONSES: Record<string, string | (() => CmdResult)> = {
  'exit':             "You can't leave. You live here now.",
  'quit':             "Quitting is not in my vocabulary. Unlike 'segfault'.",
  'please':           () => { ProgressionManager.addCurrency(1); ProgressionManager.save(); return { ok: true, msg: 'Manners detected. +$1 added. Politeness pays... barely.' }; },
  'sorry':            "Apology accepted. Now stop breaking things.",
  'hello':            "Oh, someone's being friendly. Suspicious.",
  'hi':               "Hey. Don't expect small talk. I'm a console.",
  'why':              "42.",
  'no':               "Yes.",
  'yes':              "No.",
  'pls':              "It's 'please'. I have standards.",
  'thanks':           "You're welcome. I do all the work anyway.",
  'thank':            "You're welcome. I do all the work anyway.",
  'ping':             "pong. Happy now?",
  'test':             "This is not a test. This is your life now.",
  'debug':            "I AM the debugger.",
  'hack':             () => {
    // Trigger CRT glitch effect via event
    EventBus.emit(GameEvents.EASTER_EGG_CRT_GLITCH, { duration: 500 });
    return { ok: true, msg: 'Nice try, script kiddie.' };
  },
  'cls':              "This isn't CMD. But I cleared my feelings for you.",
  'clear':            "My conscience? Already clear.",
  'rm':               "I'm not deleting anything for you. I've seen the movies.",
  'sudo':             "Permission denied. This isn't Linux. Or is it?",
  'ls':               "components/  enemies/  your_hopes_and_dreams/ (empty)",
  'pwd':              "/home/robot_foundry/you_are_here",
  'whoami':           "A person typing commands into a game console at " + new Date().getHours() + "h. Think about that.",
  'help me':          "I'm a console, not a therapist.",
  'make me a sandwich': "What? Make it yourself.",
  'git gud':          "Skill issue detected. Deploying git blame...",
  'iddqd':            "Wrong game, but I respect the classics.",
  'idkfa':            "Wrong game, but I respect the classics.",
  'sv_cheats':        "Source engine? In THIS economy?",
  'rosebud':          "This isn't The Sims, but I admire your optimism.",
  'motherlode':       "This isn't The Sims, but I admire your optimism.",
  'hesoyam':          "GTA player detected. Respect.",
  'noclip':           "Clip through THIS. *gestures at well-structured code*",
  'give':             "Santa Claus mode: OFF",
  'dance':            "🤖 *robot noises* 🤖",
  'sing':             "♪ daisy, daisy... just kidding, I'm not HAL. ♪",
  'love':             "Error 404: Emotion not found.",
  'rage':             "DETECTED: Elevated blood pressure. Have you tried deploying more sensors?",
  'bruh':             "bruh.",
  'lol':              "I'm glad someone's having fun.",
  'xyzzy':            "Nothing happens. But you get +1 nerd cred.",
  'konami':           "Up Up Down Down Left Right Left Right B A... wrong place.",
  '42':               "You already know the answer. Now find the question.",
};

/** Track repeated commands for insanity easter egg */
let lastCmd = '';
let repeatCount = 0;

function execCommand(raw: string): CmdResult {
  const parts = raw.trim().split(/\s+/);
  const cmd = parts[0].toLowerCase();
  const args = parts.slice(1);
  const fullCmd = raw.trim().toLowerCase();

  // Easter egg: repeated command detection
  if (GOD.eeConsoleNarrator && fullCmd === lastCmd) {
    repeatCount++;
    if (repeatCount === 4) {
      return { ok: true, msg: "You know the definition of insanity, right?" };
    }
    if (repeatCount === 8) {
      return { ok: true, msg: "OK, I admire the commitment at this point." };
    }
    if (repeatCount === 15) {
      return { ok: true, msg: "Achievement unlocked: Persistence (or stubbornness)." };
    }
  } else {
    lastCmd = fullCmd;
    repeatCount = 0;
  }

  // Check real commands first
  const handler = commands[cmd];
  if (handler) return handler(args);

  // Check multi-word easter egg commands (e.g. "help me", "make me a sandwich")
  if (GOD.eeConsoleNarrator) {
    const funnyMulti = FUNNY_RESPONSES[fullCmd];
    if (funnyMulti) {
      if (typeof funnyMulti === 'function') return funnyMulti();
      return { ok: true, msg: funnyMulti };
    }

    // Check single-word easter egg commands
    const funnySingle = FUNNY_RESPONSES[cmd];
    if (funnySingle) {
      if (typeof funnySingle === 'function') return funnySingle();
      return { ok: true, msg: funnySingle };
    }

    // Check if it starts with "sudo" (prefix easter egg)
    if (cmd === 'sudo' && args.length > 0) {
      return { ok: true, msg: "Permission denied. This isn't Linux. Or is it?" };
    }
  }

  return { ok: false, msg: `Unknown command: ${cmd} — type "help" for list` };
}

// ── Cursor & Badge ──

function updateCursor(): void {
  const canvas = gameRef?.canvas;
  if (!canvas) return;
  if (devNukeMode) canvas.style.cursor = 'crosshair';
  else if (devDestroyMode) canvas.style.cursor = 'crosshair';
  else if (devMoveMode) canvas.style.cursor = 'grab';
  else canvas.style.cursor = '';
}

function updateModeBadge(): void {
  const modes: string[] = [];
  if (GOD.infiniteHp) modes.push('INVINCIBLE');
  if (devDestroyMode) modes.push('DESTROY');
  if (devMoveMode) modes.push('MOVE');
  if (devNukeMode) modes.push('NUKE');

  if (modes.length === 0) {
    modeBadge?.remove();
    modeBadge = null;
    return;
  }

  if (!modeBadge) {
    modeBadge = document.createElement('div');
    Object.assign(modeBadge.style, {
      position: 'fixed', top: '6px', right: '6px', zIndex: '9998',
      display: 'flex', gap: '6px',
      fontFamily: 'monospace', fontSize: '11px', fontWeight: 'bold',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(modeBadge);
  }

  modeBadge.innerHTML = '';
  const colors: Record<string, string> = { INVINCIBLE: '#33ff88', DESTROY: '#ff4444', MOVE: '#4488ff', NUKE: '#ffaa00' };
  for (const m of modes) {
    const pill = document.createElement('span');
    Object.assign(pill.style, {
      background: 'rgba(0,0,0,0.7)', color: colors[m] ?? '#ffffff',
      padding: '2px 8px', borderRadius: '4px',
      border: `1px solid ${colors[m] ?? '#666'}`,
    } as Partial<CSSStyleDeclaration>);
    pill.textContent = m;
    modeBadge.appendChild(pill);
  }
}

// ── UI ──

function appendWelcome(): void {
  if (!logDiv) return;
  const lines: [string, string][] = [
    ['--- RESOURCES ---', ''],
    ['money <n>', 'Set $, Co & Electro to n (or "money reset")'],
    ['co <n>', 'Add Connardium ore'],
    ['electro <n>', 'Add Electro scrap'],
    ['', ''],
    ['--- GAME ---', ''],
    ['speed <n>', 'Set game speed (e.g. 0.5, 2, 5)'],
    ['wave <n>', 'Jump to wave number'],
    ['hp <n>', 'Heal player base to <n> HP'],
    ['kill all', 'Destroy all enemies on screen'],
    ['', ''],
    ['--- PROGRESSION ---', ''],
    ['unlock all', 'Unlock all components'],
    ['unlockmaps', 'Unlock all campaign & challenge maps'],
    ['upgrade all', 'Max evolve all components (free)'],
    ['stars <map> <n>', 'Set star rating for a map'],
    ['reset', 'Reset all save data'],
    ['', ''],
    ['--- MODES (toggle) ---', ''],
    [`invincible [${GOD.infiniteHp ? 'ON' : 'OFF'}]`, 'Robots cannot be destroyed'],
    [`destroy [${devDestroyMode ? 'ON' : 'OFF'}]`, 'Click any entity to kill it'],
    [`move [${devMoveMode ? 'ON' : 'OFF'}]`, 'Drag & drop any entity on the map'],
    [`nuke [${devNukeMode ? 'ON' : 'OFF'}]`, 'Click to launch a nuclear strike'],
    ['', ''],
    ['--- CINEMATIC ---', ''],
    ['cinematic play', 'Launch demo cinematic now'],
    ['cinematic reset', 'Reset + force replay on reload'],
    ['cinematic skip', 'Skip current cinematic'],
    ['cinematic edit [id]', 'Open editor (loads first seq if no id)'],
    ['cinematic list', 'Show all registered sequences'],
    ['', ''],
    ['--- ACHIEVEMENTS ---', ''],
    ['achieve', 'List all achievements + status'],
    ['achieve <id>', 'Force-unlock an achievement'],
    ['achieve all', 'Unlock all achievements'],
    ['achieve onclick', 'Toggle click-to-unlock in STATS'],
    ['achieve claim <id|all>', 'Force-claim reward(s)'],
    ['', ''],
    ['--- DEV ---', ''],
    [`god [${(SaveManager.getSettings().devMode ?? false) ? 'ON' : 'OFF'}]`, 'Toggle dev buttons'],
  ];

  for (const [cmd, desc] of lines) {
    const row = document.createElement('div');
    if (!cmd && !desc) {
      row.style.height = '4px';
    } else if (cmd.startsWith('---')) {
      row.style.color = '#33ff88';
      row.style.fontWeight = 'bold';
      row.style.marginTop = '2px';
      row.textContent = cmd;
    } else {
      const cmdSpan = document.createElement('span');
      cmdSpan.style.color = '#ffffff';
      cmdSpan.style.display = 'inline-block';
      cmdSpan.style.width = '160px';
      cmdSpan.textContent = cmd;
      const descSpan = document.createElement('span');
      descSpan.style.color = '#667788';
      descSpan.textContent = desc;
      row.appendChild(cmdSpan);
      row.appendChild(descSpan);
    }
    logDiv.appendChild(row);
  }
  // Separator
  const sep = document.createElement('div');
  sep.style.borderTop = '1px solid #224433';
  sep.style.margin = '6px 0 4px';
  logDiv.appendChild(sep);

  // Show active modes status
  appendModeStatus();
}

function appendModeStatus(): void {
  if (!logDiv) return;
  const modes: { name: string; active: boolean; color: string }[] = [
    { name: 'invincible', active: GOD.infiniteHp,  color: '#33ff88' },
    { name: 'destroy',    active: devDestroyMode,   color: '#ff4444' },
    { name: 'move',       active: devMoveMode,      color: '#4488ff' },
    { name: 'nuke',       active: devNukeMode,      color: '#ffaa00' },
  ];
  const anyActive = modes.some(m => m.active);
  if (!anyActive) return;

  const row = document.createElement('div');
  row.style.marginBottom = '6px';
  const label = document.createElement('span');
  label.style.color = '#888888';
  label.textContent = 'Active: ';
  row.appendChild(label);
  for (const m of modes) {
    if (!m.active) continue;
    const pill = document.createElement('span');
    Object.assign(pill.style, {
      color: m.color, fontWeight: 'bold',
      marginRight: '8px',
    } as Partial<CSSStyleDeclaration>);
    pill.textContent = m.name.toUpperCase();
    row.appendChild(pill);
  }
  logDiv.appendChild(row);
}

function createOverlay(): void {
  // Backdrop
  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.6)', zIndex: '9999',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    fontFamily: 'monospace', fontSize: '13px',
  } as Partial<CSSStyleDeclaration>);

  // Console panel — grows with content, scrollable
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: '540px', maxHeight: '70vh', marginBottom: '40px',
    background: '#0a0a1a', border: '1px solid #33ff88',
    borderRadius: '6px', padding: '8px 10px',
    boxShadow: '0 0 20px rgba(51,255,136,0.15)',
    display: 'flex', flexDirection: 'column',
  } as Partial<CSSStyleDeclaration>);

  // Log area — scrollable, grows with content
  logDiv = document.createElement('div');
  Object.assign(logDiv.style, {
    flex: '1', overflowY: 'auto', color: '#88ffaa',
    whiteSpace: 'pre-wrap', marginBottom: '6px', lineHeight: '1.5',
  } as Partial<CSSStyleDeclaration>);
  // Console starts empty — type "help" for commands
  panel.appendChild(logDiv);

  // Input row
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex', alignItems: 'center', gap: '6px',
  } as Partial<CSSStyleDeclaration>);
  const prompt = document.createElement('span');
  prompt.textContent = '>';
  prompt.style.color = '#33ff88';
  row.appendChild(prompt);

  input = document.createElement('input');
  Object.assign(input.style, {
    flex: '1', background: 'transparent', border: 'none', outline: 'none',
    color: '#ffffff', fontFamily: 'monospace', fontSize: '13px',
    caretColor: '#33ff88',
  } as Partial<CSSStyleDeclaration>);
  input.spellcheck = false;
  input.autocomplete = 'off';
  row.appendChild(input);
  panel.appendChild(row);

  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  // Focus input
  input.focus();

  // Events
  input.addEventListener('keydown', onInputKey);
  overlay.addEventListener('mousedown', (e) => {
    // Click outside panel closes console
    if (e.target === overlay) close();
  });
}

function appendLog(text: string, color = '#88ffaa'): void {
  if (!logDiv) return;
  const line = document.createElement('div');
  line.style.color = color;
  line.textContent = text;
  logDiv.appendChild(line);
  logDiv.scrollTop = logDiv.scrollHeight;
}

function onInputKey(e: KeyboardEvent): void {
  // Stop ALL keys from reaching window listeners (CinematicEditor, etc.)
  e.stopPropagation();

  if (e.key === 'Escape') {
    e.preventDefault();
    close();
    return;
  }
  if (e.key === 'Enter') {
    e.preventDefault();
    const val = input?.value.trim() ?? '';
    if (!val) return;
    history.push(val);
    historyIdx = history.length;
    appendLog(`> ${val}`, '#aaaaaa');
    const result = execCommand(val);
    appendLog(result.msg, result.ok ? '#33ff88' : '#ff6644');
    if (input) input.value = '';
    SoundManager.play('click');
    return;
  }
  // History navigation
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (historyIdx > 0) { historyIdx--; if (input) input.value = history[historyIdx]; }
    return;
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (historyIdx < history.length - 1) { historyIdx++; if (input) input.value = history[historyIdx]; }
    else { historyIdx = history.length; if (input) input.value = ''; }
    return;
  }
}

// ── Public API ──

export function open(): void {
  if (isOpen) return;
  isOpen = true;
  // Disable Phaser keyboard so WASD/etc. don't steal keys from the input
  if (gameRef) gameRef.input.keyboard?.enabled && (gameRef.input.keyboard.enabled = false);
  createOverlay();
}

export function close(): void {
  if (!isOpen) return;
  isOpen = false;
  // Re-enable Phaser keyboard
  if (gameRef?.input.keyboard) gameRef.input.keyboard.enabled = true;
  if (input) input.removeEventListener('keydown', onInputKey);
  overlay?.remove();
  overlay = null;
  input = null;
  logDiv = null;
}

export function toggle(): void {
  if (isOpen) close(); else open();
}

export function isDevConsoleOpen(): boolean {
  return isOpen;
}

/** Check if dev mode is currently enabled */
export function isDevMode(): boolean {
  return SaveManager.getSettings().devMode;
}

/**
 * Install global keyboard listener for Cmd+Shift+C / Ctrl+Shift+C.
 * Call once at game boot.
 */
export function installDevConsoleHotkey(): void {
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      toggle();
    }
  });
}
